import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Text } from '../../ui/typography/Text';

interface ResultsGraphProps {
  results: Array<{
    streak: number;
    occurrences: number;
    successes: number;
    probability: number;
  }>;
}

// Known human perception data (based on psychological studies)
const humanPerception = [
  { streak: 0, probability: 0.50 },
  { streak: 1, probability: 0.40 },
  { streak: 2, probability: 0.35 },
  { streak: 3, probability: 0.25 },
  { streak: 4, probability: 0.20 }
];

export const ResultsGraph: React.FC<ResultsGraphProps> = ({ results }) => {
  const getChartData = () => {
    return humanPerception.map((perception, index) => ({
      streak: perception.streak,
      simulated: results[index]?.probability || 0.5,
      perceived: perception.probability
    }));
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const fontSize = isMobile ? '0.75rem' : '0.875rem';

  return (
    <div className="space-y-2">      
      <div className="text-center mb-4">
        <Text size="lg" className="font-medium text-gray-800">
          Probability of Getting Heads After Seeing a Streak
        </Text>
        <Text className="text-sm text-gray-600">
          Comparing simulated probability vs. common human expectations
        </Text>
      </div>

      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart 
            data={getChartData()} 
            margin={{ 
              top: 20,
              right: 30,
              left: 35,
              bottom: 30
            }}
          >
            <XAxis 
              dataKey="streak"
              tick={{ fontSize }}
              domain={[0, 4]}
              ticks={[0, 1, 2, 3, 4]}
            />
            <YAxis 
              domain={[0, 1]}
              label={{ 
                value: 'Probability',
                angle: -90, 
                position: 'insideLeft',
                offset: 5,
                style: {
                  textAnchor: 'middle',
                  fontSize
                }
              }}
              tick={{ fontSize }}
              tickFormatter={(value) => value.toFixed(1)}
            />
            <ReferenceLine y={0.5} stroke="#9CA3AF" strokeDasharray="3 3" />
            {!isMobile && (
              <Tooltip 
                formatter={(value: number) => `${(value * 100).toFixed(1)}%`}
                labelFormatter={(value) => `After ${value} heads`}
              />
            )}
            <Line 
              type="monotone" 
              dataKey="simulated" 
              stroke="#6366f1" 
              strokeWidth={3} 
              dot={false}
              name="Simulation Results"
            />
            <Line 
              type="monotone" 
              dataKey="perceived" 
              stroke="#f97316" 
              strokeWidth={3} 
              strokeDasharray="5 5"
              dot={false}
              name="Typical Human Perception"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="text-center">
        <Text className="font-medium text-gray-700 text-sm">
          Consecutive Heads Seen
        </Text>
      </div>

      <div className="sm:flex hidden flex-row justify-center items-center gap-3 sm:gap-6 px-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500" />
          <span className="text-sm text-gray-700">Simulated Probability</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-orange-500" />
          <span className="text-sm text-gray-700">Human Perception</span>
        </div>
      </div>
      
      <Text className="text-xs text-gray-500 text-center italic mt-2">
        Note: Human perception data is based on psychological studies of the Gambler's Fallacy
      </Text>
    </div>
  );
}; 